import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ProjectCard, ProjectCardSkeleton } from '../components/ProjectCard';
import {
  Rocket,
  Zap,
  Users,
  ArrowRight,
  Globe,
  MessageSquare,
  BarChart3,
  FileText,
  Heart,
  Send,
  TrendingUp,
  UserPlus,
  Lightbulb,
  GraduationCap,
  Palette,
  UsersRound,
  EyeOff,
  HelpCircle,
  BatteryLow
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../context/LanguageContext';
import { projectAPI } from '../lib/api';
import { useState, useEffect } from 'react';

export function LandingPage() {
  const { isAuthenticated } = useAuth();
  const { t } = useTranslation();
  const [featuredProjects, setFeaturedProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await projectAPI.list({ feed: 'trending', limit: 6 });
        setFeaturedProjects(response.data.projects || []);
      } catch (error) {
        console.error('Failed to fetch projects:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProjects();
  }, []);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative overflow-hidden hero-gradient">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-orange-50 border border-orange-200">
                <Zap className="w-4 h-4 text-orange-500" />
                <span className="text-sm font-medium text-orange-700">
                  {t('landing.badge')}
                </span>
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 leading-tight">
                {t('landing.title1')}
                <span className="text-gradient">{t('landing.titleHighlight')}</span>
                {t('landing.title2')}
              </h1>

              <p className="text-lg sm:text-xl text-slate-600 max-w-xl">
                {t('landing.subtitle')}
              </p>

              <div className="flex flex-wrap gap-4">
                <Link to={isAuthenticated ? '/submit' : '/signup'}>
                  <Button className="btn-boost text-lg px-8 py-6" data-testid="hero-cta-btn">
                    {t('landing.getStarted')}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <Link to="/discover">
                  <Button variant="outline" className="text-lg px-8 py-6 rounded-full">
                    {t('landing.seeTrending')}
                  </Button>
                </Link>
              </div>

              <p className="text-sm text-slate-400">{t('landing.noCode')}</p>
            </div>

            {/* Hero Visual - Mockup */}
            <div className="relative hidden lg:block">
              <div className="absolute inset-0 bg-gradient-to-br from-orange-500/10 to-red-500/10 rounded-3xl blur-3xl" />
              <div className="relative bg-white rounded-2xl shadow-2xl border border-slate-200 p-6 space-y-4">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-400 to-red-500" />
                  <div>
                    <div className="font-bold text-slate-900 text-sm">EcoTrack App</div>
                    <div className="text-xs text-slate-400">by Sarah K.</div>
                  </div>
                </div>
                <div className="h-32 rounded-xl bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center">
                  <Globe className="w-12 h-12 text-emerald-400" />
                </div>
                <p className="text-sm text-slate-600">Track your carbon footprint with AI-powered suggestions for a greener lifestyle.</p>
                <div className="flex items-center gap-6 pt-2">
                  <StatBadge icon={Heart} label="312 supporters" color="text-red-500" />
                  <StatBadge icon={MessageSquare} label="84 feedback" color="text-blue-500" />
                  <StatBadge icon={Users} label="27 waitlist" color="text-green-500" />
                </div>
                <div className="flex items-center gap-2 pt-2">
                  <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full w-3/4 bg-gradient-to-r from-orange-400 to-red-500 rounded-full" />
                  </div>
                  <span className="text-xs font-mono font-bold text-orange-600">748 momentum</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              {t('landing.problemTitle')}
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              {t('landing.problemDesc')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <ProblemCard
              icon={EyeOff}
              title={t('landing.problemNoAudience')}
              description={t('landing.problemNoAudienceDesc')}
            />
            <ProblemCard
              icon={HelpCircle}
              title={t('landing.problemNoSignal')}
              description={t('landing.problemNoSignalDesc')}
            />
            <ProblemCard
              icon={BatteryLow}
              title={t('landing.problemNoMomentum')}
              description={t('landing.problemNoMomentumDesc')}
            />
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              {t('landing.solutionTitle')}
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <SolutionCard
              icon={FileText}
              title={t('landing.solIdeaPage')}
              description={t('landing.solIdeaPageDesc')}
              color="orange"
            />
            <SolutionCard
              icon={Heart}
              title={t('landing.solSupporters')}
              description={t('landing.solSupportersDesc')}
              color="red"
            />
            <SolutionCard
              icon={MessageSquare}
              title={t('landing.solFeedback')}
              description={t('landing.solFeedbackDesc')}
              color="blue"
            />
            <SolutionCard
              icon={BarChart3}
              title={t('landing.solTraction')}
              description={t('landing.solTractionDesc')}
              color="green"
            />
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              {t('landing.howTitle')}
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <StepCard
              number="1"
              icon={Send}
              title={t('landing.howStep1Title')}
              description={t('landing.howStep1Desc')}
            />
            <StepCard
              number="2"
              icon={TrendingUp}
              title={t('landing.howStep2Title')}
              description={t('landing.howStep2Desc')}
            />
            <StepCard
              number="3"
              icon={Rocket}
              title={t('landing.howStep3Title')}
              description={t('landing.howStep3Desc')}
            />
          </div>
        </div>
      </section>

      {/* Who Is It For */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              {t('landing.whoTitle')}
            </h2>
          </div>

          <div className="flex flex-wrap justify-center gap-4">
            <PersonaChip icon={Lightbulb} label={t('landing.whoFounders')} desc={t('landing.whoFoundersDesc')} />
            <PersonaChip icon={Rocket} label={t('landing.whoIndie')} desc={t('landing.whoIndieDesc')} />
            <PersonaChip icon={GraduationCap} label={t('landing.whoStudents')} desc={t('landing.whoStudentsDesc')} />
            <PersonaChip icon={Palette} label={t('landing.whoCreators')} desc={t('landing.whoCreatorsDesc')} />
            <PersonaChip icon={UsersRound} label={t('landing.whoTeams')} desc={t('landing.whoTeamsDesc')} />
          </div>
        </div>
      </section>

      {/* Featured Projects / Trending Ideas */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-12">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-2">
                {t('landing.trendingProjects')}
              </h2>
              <p className="text-slate-600">
                {t('landing.trendingDesc')}
              </p>
            </div>
            <Link to="/discover">
              <Button variant="outline" className="rounded-full">
                {t('landing.viewAll')}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {loading ? (
              <>
                <ProjectCardSkeleton />
                <ProjectCardSkeleton />
                <ProjectCardSkeleton />
              </>
            ) : featuredProjects.length > 0 ? (
              featuredProjects.map((project) => (
                <ProjectCard key={project.project_id} project={project} />
              ))
            ) : (
              <div className="col-span-3 text-center py-16">
                <Rocket className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                <h3 className="text-xl font-semibold text-slate-700 mb-2">
                  {t('landing.noProjects')}
                </h3>
                <p className="text-slate-500 mb-6">
                  {t('landing.beFirst')}
                </p>
                <Link to="/submit">
                  <Button className="btn-boost">{t('landing.submitYourProject')}</Button>
                </Link>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-slate-900 to-slate-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            {t('landing.ctaTitle')}
          </h2>
          <p className="text-xl text-slate-300 mb-8">
            {t('landing.ctaDesc')}
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to={isAuthenticated ? '/submit' : '/signup'}>
              <Button className="btn-boost text-lg px-8 py-6">
                {isAuthenticated ? t('landing.submitYourProject') : t('landing.getStarted')}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/discover">
              <Button variant="outline" className="text-lg px-8 py-6 rounded-full border-slate-600 text-white hover:bg-slate-800">
                {t('landing.exploreProjects')}
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function StatBadge({ icon: Icon, label, color }) {
  return (
    <div className="flex items-center gap-1.5">
      <Icon className={`w-3.5 h-3.5 ${color}`} />
      <span className="text-xs text-slate-500">{label}</span>
    </div>
  );
}

function ProblemCard({ icon: Icon, title, description }) {
  return (
    <div className="text-center p-8 rounded-2xl bg-slate-50 border border-slate-100">
      <div className="w-14 h-14 rounded-2xl bg-red-50 flex items-center justify-center mx-auto mb-5">
        <Icon className="w-7 h-7 text-red-400" />
      </div>
      <h3 className="text-xl font-bold text-slate-900 mb-2">{title}</h3>
      <p className="text-slate-600">{description}</p>
    </div>
  );
}

function SolutionCard({ icon: Icon, title, description, color }) {
  const colors = {
    orange: 'bg-orange-500',
    red: 'bg-red-500',
    blue: 'bg-blue-500',
    green: 'bg-green-500',
  };

  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 hover:shadow-md transition-shadow">
      <div className={`w-12 h-12 rounded-xl ${colors[color]} flex items-center justify-center mb-4`}>
        <Icon className="w-6 h-6 text-white" />
      </div>
      <h3 className="text-lg font-bold text-slate-900 mb-2">{title}</h3>
      <p className="text-sm text-slate-600">{description}</p>
    </div>
  );
}

function StepCard({ number, icon: Icon, title, description }) {
  return (
    <div className="text-center p-8">
      <div className="relative w-16 h-16 mx-auto mb-6">
        <div className="absolute inset-0 bg-orange-100 rounded-2xl rotate-6" />
        <div className="relative w-full h-full bg-gradient-to-br from-orange-400 to-red-500 rounded-2xl flex items-center justify-center">
          <Icon className="w-7 h-7 text-white" />
        </div>
        <div className="absolute -top-2 -right-2 w-7 h-7 bg-slate-900 rounded-full flex items-center justify-center">
          <span className="text-xs font-bold text-white">{number}</span>
        </div>
      </div>
      <h3 className="text-xl font-bold text-slate-900 mb-2">{title}</h3>
      <p className="text-slate-600">{description}</p>
    </div>
  );
}

function PersonaChip({ icon: Icon, label, desc }) {
  return (
    <div className="flex items-center gap-3 bg-white rounded-2xl px-6 py-4 shadow-sm border border-slate-100 hover:shadow-md transition-shadow">
      <div className="w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center flex-shrink-0">
        <Icon className="w-5 h-5 text-orange-500" />
      </div>
      <div>
        <div className="font-semibold text-slate-900 text-sm">{label}</div>
        <div className="text-xs text-slate-500">{desc}</div>
      </div>
    </div>
  );
}
